import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkles, ChevronRight, Check } from "lucide-react";
import { useLangPrefix, withLangPrefix } from "../hooks/useLangPrefix";

/**
 * NewFormationPopup — announces the "Cover Multisupport®" formation.
 *
 * Behaviour:
 * - Opens automatically ~2 s after first mount of the app if:
 *   • the user hasn't dismissed it before (localStorage flag), AND
 *   • the announce campaign ID matches the stored one (so we can re-fire
 *     the popup for a new formation later just by bumping CAMPAIGN_ID).
 * - "Ne plus afficher" checkbox marks the campaign as dismissed forever.
 * - Otherwise closing the popup only hides it for the current session.
 * - Clicking the primary CTA navigates to the formation detail page and
 *   marks the campaign as dismissed (they've seen it).
 */
const CAMPAIGN_ID = "cover-multisupport-2026-v1";
const STORAGE_KEY = `popup:formation:${CAMPAIGN_ID}`;
const SESSION_KEY = `popup:formation:session:${CAMPAIGN_ID}`;
const COURSE_URL = "/formation/08afa93c-8c71-45d3-9c02-4e6d5c6d0c02";
const COVER_IMAGE = "/formations/cover-multisupport.png";

export const NewFormationPopup = () => {
  // Popup désactivée au profit du bandeau d'annonce administrable (top header).
  return null;
};

const NewFormationPopupLegacy = () => {
  const { lang } = useLangPrefix();
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Do not show if user already opted out permanently or already closed
    // it in this browser session.
    if (localStorage.getItem(STORAGE_KEY) === "dismissed") return;
    if (sessionStorage.getItem(SESSION_KEY) === "closed") return;
    const t = setTimeout(() => setOpen(true), 1800);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    if (dontShow) {
      localStorage.setItem(STORAGE_KEY, "dismissed");
    } else {
      sessionStorage.setItem(SESSION_KEY, "closed");
    }
  };

  const goToFormation = () => {
    // Whether or not "don't show" was checked, we don't want to re-nag the
    // same visitor after they clicked through.
    localStorage.setItem(STORAGE_KEY, "dismissed");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
      data-testid="new-formation-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-formation-popup-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Fermer la fenêtre"
        onClick={close}
        className="absolute inset-0 bg-[#0E1426]/72 backdrop-blur-sm"
      />

      {/* Card */}
      <div className="relative w-full max-w-2xl bg-[#FAF7F0] rounded-2xl shadow-2xl shadow-black/40 overflow-hidden animate-fade-in border border-[#C9B896]/40">
        <button
          type="button"
          onClick={close}
          aria-label="Fermer"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-[#1F2A4A] flex items-center justify-center shadow-sm"
          data-testid="popup-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-5">
          {/* Visual */}
          <div className="relative md:col-span-2 h-40 md:h-auto min-h-[220px] bg-[#1F2A4A]">
            <img
              src={COVER_IMAGE}
              alt="Cover Multisupport — Sellerie Académie"
              className="w-full h-full object-cover opacity-90"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#1F2A4A]/70 via-[#1F2A4A]/20 to-transparent" />
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-[#C9B896] to-[#E0D4BA] text-[#1F2A4A] text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1 rounded-full shadow-lg">
              <Sparkles className="w-3 h-3" strokeWidth={2.4} />
              Nouveauté
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-3 p-6 md:p-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] font-semibold mb-2">
              Nouvelle formation
            </p>
            <h2
              id="new-formation-popup-title"
              className="font-light text-2xl md:text-3xl leading-tight text-[#1F2A4A] mb-3"
            >
              Recovering <span className="font-serif italic">& Garnissage Multisupports</span>
            </h2>
            <p className="text-sm text-[#4A5568] font-light leading-relaxed mb-4">
              Une méthode complète pour habiller cuir, plastique, bois, aluminium, verre, mobilier, nautisme, moto, automobile… <b className="text-[#1F2A4A]">Donnez une nouvelle vie, avec un nouveau style&nbsp;!</b>
            </p>

            <ul className="text-xs text-[#1F2A4A]/85 space-y-1.5 mb-5">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#C9B896]" strokeWidth={2.5} />15 jours &middot; 105 h &middot; 2 stagiaires max</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#C9B896]" strokeWidth={2.5} />6 990&nbsp;€ TTC / personne</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#C9B896]" strokeWidth={2.5} />Modules à la carte dès 1 500&nbsp;€</li>
            </ul>

            <Link
              to={withLangPrefix(COURSE_URL, lang)}
              onClick={goToFormation}
              className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto bg-[#1F2A4A] hover:bg-[#141C2E] text-white rounded-full px-6 h-11 text-[11px] uppercase tracking-[0.2em] font-semibold shadow-md transition-colors"
              data-testid="popup-cta"
            >
              Découvrir la formation
              <ChevronRight className="w-4 h-4" />
            </Link>

            <label className="mt-5 flex items-center gap-2 text-[11px] text-[#4A5568] cursor-pointer select-none" data-testid="popup-dont-show-label">
              <input
                type="checkbox"
                checked={dontShow}
                onChange={(e) => setDontShow(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#C9B896]"
                data-testid="popup-dont-show-checkbox"
              />
              <span>Ne plus afficher cette annonce</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewFormationPopup;
